export interface LatLngPoint {
  lat: number;
  lng: number;
}

export interface RouteResult {
  distanceMeters: number;
  durationSeconds: number;
  estimatedKms: number;
  path: LatLngPoint[];
}

/**
 * Decodes Google Encoded Polyline algorithm string to an array of LatLngLiteral coordinates.
 */
export function decodePolyline(encoded: string): LatLngPoint[] {
  const points: LatLngPoint[] = [];
  let index = 0;
  const len = encoded.length;
  let lat = 0;
  let lng = 0;

  while (index < len) {
    let b: number;
    let shift = 0;
    let result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = (result & 1) ? ~(result >> 1) : (result >> 1);
    lat += dlat;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = (result & 1) ? ~(result >> 1) : (result >> 1);
    lng += dlng;

    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }

  return points;
}

/**
 * Computes driving directions using Google's modern Routes API (v2:computeRoutes).
 * Falls back to legacy DirectionsService if needed.
 */
export async function computeRoute(
  origin: LatLngPoint,
  destination: LatLngPoint,
  waypoints: LatLngPoint[] = [],
  apiKey: string
): Promise<RouteResult> {
  // 1. Try modern Routes API (POST https://routes.googleapis.com/directions/v2:computeRoutes)
  if (apiKey) {
    try {
      const response = await fetch('https://routes.googleapis.com/directions/v2:computeRoutes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey,
          'X-Goog-FieldMask': 'routes.distanceMeters,routes.duration,routes.polyline.encodedPolyline',
        },
        body: JSON.stringify({
          origin: {
            location: {
              latLng: {
                latitude: origin.lat,
                longitude: origin.lng,
              },
            },
          },
          destination: {
            location: {
              latLng: {
                latitude: destination.lat,
                longitude: destination.lng,
              },
            },
          },
          intermediates: waypoints.map((wp) => ({
            location: {
              latLng: {
                latitude: wp.lat,
                longitude: wp.lng,
              },
            },
          })),
          travelMode: 'DRIVE',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const firstRoute = data.routes?.[0];
        if (firstRoute) {
          const distanceMeters = Number(firstRoute.distanceMeters || 0);
          const rawDuration = firstRoute.duration || '0s';
          const durationSeconds = parseInt(rawDuration.replace('s', ''), 10) || 0;
          const encodedPolyline = firstRoute.polyline?.encodedPolyline || '';
          const path = encodedPolyline ? decodePolyline(encodedPolyline) : [];

          return {
            distanceMeters,
            durationSeconds,
            estimatedKms: Number((distanceMeters / 1000).toFixed(2)),
            path,
          };
        }
      } else {
        console.warn('Routes API returned status:', response.status);
      }
    } catch (apiErr) {
      console.warn('Routes API fetch error, attempting fallback:', apiErr);
    }
  }

  // 2. Fallback to legacy DirectionsService if available in window.google.maps
  if (window.google?.maps?.DirectionsService) {
    const directionsService = new google.maps.DirectionsService();
    const result = await new Promise<google.maps.DirectionsResult>((resolve, reject) => {
      directionsService.route(
        {
          origin: new google.maps.LatLng(origin.lat, origin.lng),
          destination: new google.maps.LatLng(destination.lat, destination.lng),
          waypoints: waypoints.map((wp) => ({
            location: new google.maps.LatLng(wp.lat, wp.lng),
            stopover: true,
          })),
          travelMode: google.maps.TravelMode.DRIVING,
          optimizeWaypoints: true,
        },
        (res, status) => {
          if (status === google.maps.DirectionsStatus.OK && res) {
            resolve(res);
          } else {
            reject(new Error(`Directions status: ${status}`));
          }
        }
      );
    });

    const route = result.routes[0];
    const distanceMeters = route.legs.reduce((sum, leg) => sum + (leg.distance?.value || 0), 0);
    const durationSeconds = route.legs.reduce((sum, leg) => sum + (leg.duration?.value || 0), 0);
    const path = route.overview_path.map((pt) => ({ lat: pt.lat(), lng: pt.lng() }));

    return {
      distanceMeters,
      durationSeconds,
      estimatedKms: Number((distanceMeters / 1000).toFixed(2)),
      path,
    };
  }

  throw new Error('No routing service available.');
}
